import { useCallback, useRef, useState } from "react";
import { DriverSection } from "../../redux/onBoardingSlice.types";
import { resolveReason } from "../utils";
import { SECTION_STATUS } from "./constants";
import { SectionTrackingState } from "./types";

/**
 * Manages section review state (viewed / rejected / picker open) and all
 * related event handlers.
 *
 * Isolated from rendering so it can be tested with `renderHook` without
 * mounting any UI.
 */
export const useSectionStates = (sortedSections: DriverSection[]) => {
  const [sectionStates, setSectionStates] = useState<Record<string, SectionTrackingState>>({});
  const sectionPickerRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const init = useCallback((sections: DriverSection[]) => {
    const initial: Record<string, SectionTrackingState> = {};
    sections.forEach((s) => {
      initial[s.sectionId] = {
        status: SECTION_STATUS.UNVIEWED,
        pendingReason: "",
        pendingComment: "",
        confirmedReason: "",
        showRejectPicker: false,
      };
    });
    setSectionStates(initial);
  }, []);

  const markViewed = useCallback((sectionId: string) => {
    setSectionStates((prev) => {
      if (prev[sectionId]?.status !== SECTION_STATUS.UNVIEWED) return prev;
      return { ...prev, [sectionId]: { ...prev[sectionId], status: SECTION_STATUS.VIEWED } };
    });
  }, []);

  const handleReject = useCallback((sectionId: string) => {
    setSectionStates((prev) => ({
      ...prev,
      [sectionId]: { ...prev[sectionId], showRejectPicker: true },
    }));
    setTimeout(() => {
      sectionPickerRefs.current[sectionId]?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  }, []);

  const handleRejectConfirm = useCallback(
    (sectionId: string) => {
      setSectionStates((prev) => {
        const s = prev[sectionId];
        if (!s?.pendingReason) return prev;
        const reasons = sortedSections.find((sec) => sec.sectionId === sectionId)?.rejectionReasons ?? [];
        return {
          ...prev,
          [sectionId]: {
            ...s,
            status: SECTION_STATUS.REJECTED,
            confirmedReason: resolveReason(s.pendingReason, s.pendingComment, reasons),
            pendingReason: "",
            pendingComment: "",
            showRejectPicker: false,
          },
        };
      });
    },
    [sortedSections]
  );

  const handleRejectCancel = useCallback((sectionId: string) => {
    setSectionStates((prev) => ({
      ...prev,
      [sectionId]: { ...prev[sectionId], pendingReason: "", pendingComment: "", showRejectPicker: false },
    }));
  }, []);

  const handleSelectReason = useCallback((sectionId: string, reason: string) => {
    setSectionStates((prev) => {
      const s = prev[sectionId];
      if (!s) return prev;
      return { ...prev, [sectionId]: { ...s, pendingReason: reason, pendingComment: "" } };
    });
  }, []);

  const handleCommentChange = useCallback((sectionId: string, value: string) => {
    setSectionStates((prev) => {
      const s = prev[sectionId];
      if (!s) return prev;
      return { ...prev, [sectionId]: { ...s, pendingComment: value } };
    });
  }, []);

  const handleClearReason = useCallback((sectionId: string) => {
    setSectionStates((prev) => ({
      ...prev,
      [sectionId]: { ...prev[sectionId], pendingReason: "", pendingComment: "" },
    }));
  }, []);

  const handleUnreject = useCallback((sectionId: string) => {
    setSectionStates((prev) => ({
      ...prev,
      [sectionId]: {
        ...prev[sectionId],
        status: SECTION_STATUS.VIEWED,
        confirmedReason: "",
        showRejectPicker: false,
      },
    }));
  }, []);

  return {
    sectionStates,
    sectionPickerRefs,
    init,
    markViewed,
    handleReject,
    handleRejectConfirm,
    handleRejectCancel,
    handleSelectReason,
    handleCommentChange,
    handleClearReason,
    handleUnreject,
  };
};
